

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

export type ComboboxOption = {
  value: string;
  label: string;
  selectedLabel?: string;
};

interface MultiSelectComboBoxProps {
  options: ComboboxOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyPlaceholder?: string;
  isLoading?: boolean;
  filterSelectedOptions?: boolean;
}

export function MultiSelectComboBox({
  options,
  selected,
  onChange,
  placeholder,
  emptyPlaceholder,
  isLoading = false,
  filterSelectedOptions = false,
}: MultiSelectComboBoxProps) {
  const { t } = useTranslation();
  // Map selected IDs to option objects for Autocomplete
  const selectedOptions = React.useMemo(() =>
    options.filter(option => selected.includes(option.value)),
    [options, selected]);

  const finalPlaceholder = placeholder ?? t('ui.combobox.select_options');
  const finalEmptyPlaceholder = emptyPlaceholder ?? t('ui.combobox.no_options');

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        multiple
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOptions}
        onChange={(_, newValue) => {
          onChange(newValue.map(item => item.value));
        }}
        loading={isLoading}
        noOptionsText={finalEmptyPlaceholder}
        filterSelectedOptions={filterSelectedOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={selectedOptions.length === 0 ? finalPlaceholder : ''}
            label={selectedOptions.length > 0 ? finalPlaceholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              label={option.selectedLabel || option.label}
              {...getTagProps({ index })}
              key={option.value}
              size="small"
              variant="filled"
              color="primary"
              sx={{ borderRadius: 1 }}
            />
          ))
        }
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5,
          }
        }}
      />
    </Box>
  );
}
